import { TFunction } from 'i18next';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import * as yup from 'yup';

export enum ScteState {
  STOPPED = 0,
  WORKING = 1,
  FAILED = 2,
}

export interface Scte {
  autoStart: boolean;
  defaultName: string;
  id: number;
  inputInterface: number;
  leapSeconds: number;
  msgDeltaMs: number;
  name: string;
  preEventReminders: number;
  synchronizationType: string;
  timeDescriptorPeriodSecs: number;
  status: number | string;
  type: number;
  typeString: string;
}

export interface ScteStats {
  lastErrorOpId: string;
  lastLeapSeconds: 0;
  lastScte104Error: string;
  lastScte104ErrorCode: string;
  lastStce104Msg: string;
  rxBytes: string;
  rxCorruptMessages: string;
  rxOKMessages: string;
  state: ScteState;
  lastReset: string;
}

export const scteIsStopped = (state: ScteState): boolean => state === ScteState.STOPPED || state === ScteState.FAILED;
export const scteIsStarted = (state: ScteState): boolean => state === ScteState.WORKING || state === ScteState.FAILED;

export const scteStateToIcon = (state: ScteState): string => {
  switch (state) {
    case ScteState.STOPPED:
      return 'StatusStopped';
    case ScteState.WORKING:
      return 'StatusOK';
    case ScteState.FAILED:
      return 'StatusStreamWarning';
  }
};

export const scteStateToName = (state: ScteState, t: TFunction): string => {
  switch (state) {
    case ScteState.STOPPED:
      return t('scte.states.stopped');
    case ScteState.WORKING:
      return t('scte.states.active');
    case ScteState.FAILED:
      return t('scte.states.failed');
  }
};

const scteStateToSortable = (state: ScteState): number => {
  switch (state) {
    case ScteState.WORKING:
      return 1;
    case ScteState.FAILED:
      return 2;
    case ScteState.STOPPED:
      return 3;
  }
};

export const scteStateToColor = (state: ScteState): string => {
  switch (state) {
    case ScteState.STOPPED:
      return 'haiui-gray-07';
    case ScteState.WORKING:
      return 'haiui-green-01';
    case ScteState.FAILED:
      return 'haiui-amber-01';
  }
};

// For ActionBar status filter
export const scteStateToStatus = (state: ScteState): string => {
  switch (state) {
    case ScteState.STOPPED:
      return 'INACTIVE';
    case ScteState.WORKING:
      return 'ACTIVE';
    case ScteState.FAILED:
      return 'ALERT';
  }
};
export const scteModelToStatus = (model: ScteViewModel): string => {
  return scteStateToStatus(model.state);
};

export const scteStateStatusToState = (state: ScteState, status: number): ScteState => {
  switch (state) {
    case ScteState.STOPPED:
      return ScteState.STOPPED;
    case ScteState.WORKING:
      return status === 0 ? ScteState.FAILED : ScteState.WORKING;
    case ScteState.FAILED:
      return ScteState.FAILED;
  }
};

export const scteSyncType = (syncType: string, t: TFunction) => {
  switch (syncType) {
    case 'auto':
      return t('scte.syncTypes.auto');
    case 'alive':
      return t('scte.syncTypes.alive');
    case 'timeDescriptor':
      return t('scte.syncTypes.timeDescriptor');
    case 'system':
      return t('scte.syncTypes.system');
    default:
      return '';
  }
};

export interface ScteResponse extends GenericResponse {
  info: Scte;
  stats: ScteStats;
}

export interface GetAllScteResponse {
  data: ScteResponse[];
}

export interface ScteViewModel extends Scte {
  stats: ScteStats;
  state: ScteState;

  stateSortable: number;
  autoStartTxt: string;
  syncAutoTxt: string;
  timingTxt: string;

  status: string;
  selected: boolean /* checked by checkbox */;
  hidden: boolean /* when filtered out */;
  updated: string /* to trigger setState to detect change and re-render */;
}

export const mapScteViewModel = (response: ScteResponse, t: TFunction): ScteViewModel => ({
  ...response.info,
  stats: { ...response.stats },
  state: scteStateStatusToState(response.stats.state, Number(response.info.status)),

  stateSortable: scteStateToSortable(response.stats.state),
  autoStartTxt: response.info.autoStart ? t('general.enabled') : t('general.disabled'),
  syncAutoTxt: scteSyncType(response.info.synchronizationType, t),
  timingTxt:
    response.info.timeDescriptorPeriodSecs == 0
      ? t('general.disabled')
      : `${response.info.timeDescriptorPeriodSecs} ${t('statistics.units.sec')}`,
  typeString: `${response.info.typeString} (${systemInfoCapabilities.is2110() ? '' : 'BNC-'}${
    response.info.inputInterface + 1
  })`,

  status: scteStateToStatus(response.stats.state), // required for ActionBar
  selected: false,
  hidden: false,
  updated: new Date().toISOString(),
});

export const mapScteResponse = (response: GetAllScteResponse, t: TFunction): ScteViewModel[] => {
  const mapped = response?.data.map((item) => mapScteViewModel(item, t));
  return mapped;
};

/*
 * Validation
 */
export const scteValidationSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup.string().max(63, t('validation.maxLength', { length: 63 })),
    msgDeltaMs: yup
      .number()
      .required(t('validation.required'))
      .typeError(t('validation.scte.msgDeltaMs'))
      .min(100, t('validation.scte.msgDeltaMs'))
      .max(10000, t('validation.scte.msgDeltaMs')),
    leapSeconds: yup
      .number()
      .typeError(t('validation.scte.leapSeconds'))
      .min(0, t('validation.scte.leapSeconds'))
      .max(1000, t('validation.scte.leapSeconds')),
  });
